import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

interface SepaFilterProps {
    onFilterChange: (filters: SepaFilterState) => void;
}

export interface SepaFilterState {
    searchTerm: string;
    noBankAccount: boolean;
    noSubscription: boolean;
    inactiveNextMonth: boolean;
    clientsWithObservations: boolean;
    clientsWithReferences: boolean;
}

const SepaFilter: React.FC<SepaFilterProps> = ({ onFilterChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [noBankAccount, setNoBankAccount] = useState(false);
    const [noSubscription, setNoSubscription] = useState(false);
    const [inactiveNextMonth, setInactiveNextMonth] = useState(false);
    const [clientsWithObservations, setClientsWithObservations] = useState(false);
    const [clientsWithReferences, setClientsWithReferences] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Función para enviar los cambios al componente padre
    const notifyFilterChange = (newFilters: Partial<SepaFilterState>) => {
        const updatedFilters = {
            searchTerm,
            noBankAccount,
            noSubscription,
            inactiveNextMonth,
            clientsWithObservations,
            clientsWithReferences,
            ...newFilters
        };
        onFilterChange(updatedFilters);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        notifyFilterChange({ searchTerm: value });
    };

    const handleFilterToggle = (filterName: keyof SepaFilterState, checked: boolean) => {
        const updates: Partial<SepaFilterState> = { [filterName]: checked };
        
        switch (filterName) {
            case 'noBankAccount':
                setNoBankAccount(checked);
                break;
            case 'noSubscription':
                setNoSubscription(checked);
                break;
            case 'inactiveNextMonth':
                setInactiveNextMonth(checked);
                break;
            case 'clientsWithObservations':
                setClientsWithObservations(checked);
                break;
            case 'clientsWithReferences':
                setClientsWithReferences(checked);
                break;
        }
        
        notifyFilterChange(updates);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setNoBankAccount(false);
        setNoSubscription(false);
        setInactiveNextMonth(false);
        setClientsWithObservations(false);
        setClientsWithReferences(false);
        
        notifyFilterChange({
            searchTerm: '',
            noBankAccount: false,
            noSubscription: false,
            inactiveNextMonth: false,
            clientsWithObservations: false,
            clientsWithReferences: false
        });
    };

    const hasActiveFilters = noBankAccount || noSubscription || inactiveNextMonth || 
                            clientsWithObservations || clientsWithReferences;

    return (
        <div className="space-y-4">
            {/* Barra de búsqueda principal */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, DNI, email, teléfono, IBAN u observaciones..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
                
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                            showAdvancedFilters || hasActiveFilters
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        <Filter className="h-4 w-4 mr-2" />
                        Filtros
                        {hasActiveFilters && (
                            <span className="ml-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {[noBankAccount, noSubscription, inactiveNextMonth, clientsWithObservations, clientsWithReferences].filter(Boolean).length}
                            </span>
                        )}
                    </button>
                    
                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            className="flex items-center px-3 py-2 text-gray-500 hover:text-red-500 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
                            title="Limpiar filtros"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Filtros avanzados expandibles */}
            {showAdvancedFilters && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={noBankAccount}
                                onChange={(e) => handleFilterToggle('noBankAccount', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Sin cuenta bancaria (IBAN)</span>
                        </label>
                        
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={noSubscription}
                                onChange={(e) => handleFilterToggle('noSubscription', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Sin suscripción</span>
                        </label>
                        
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={inactiveNextMonth}
                                onChange={(e) => handleFilterToggle('inactiveNextMonth', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Inactivos próximo mes</span>
                        </label>
                        
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={clientsWithObservations}
                                onChange={(e) => handleFilterToggle('clientsWithObservations', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Con observaciones</span>
                        </label>
                        
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={clientsWithReferences}
                                onChange={(e) => handleFilterToggle('clientsWithReferences', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Con referencias</span>
                        </label>
                    </div>
                    
                    {hasActiveFilters && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">
                                    {[noBankAccount, noSubscription, inactiveNextMonth, clientsWithObservations, clientsWithReferences].filter(Boolean).length} filtro(s) activo(s)
                                </span>
                                <button
                                    onClick={clearAllFilters}
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                    Limpiar todos los filtros
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SepaFilter;